// A slot's app buttons as items in a menu the page already owns, such as a
// page's "..." menu. The page calls `useSlotButtons` itself, because it needs to
// know when an item runs, and spreads these among its own items, with dividers
// where its neighbours call for them. The icon and the tooltip mark each item as
// an app, as `AppTabLabel` does for a tab.

import { t } from '@lingui/core/macro';
import { MenuItem, Tooltip } from '@luminovo/design-system';
import { WorkflowIcon } from 'lucide-react';
import type { ReactElement } from 'react';

import type { SlotButton } from '../hooks/useSlotButtons';

/**
 * A slot's buttons as items for a menu the page owns: one `MenuItem` per button.
 * Empty when there are no buttons. A keyed array, because MUI's `Menu` refuses a
 * Fragment child. The page owns the dividers, since only it knows its neighbours.
 *
 * Every item is disabled while one of them runs, so the menu runs one at a time;
 * `disabled` adds the page's own reasons.
 */
export function renderAppMenuItems(buttons: readonly SlotButton[], options?: { disabled?: boolean }): ReactElement[] {
    const disabled = options?.disabled || buttons.some((button) => button.isRunning);
    return buttons.map((button) => (
        <MenuItem
            key={button.key}
            label={button.name}
            onClick={button.run}
            startIcon={
                // `describeChild`, so the tooltip describes the item rather than
                // becoming part of its name.
                <Tooltip title={t`This item is an app`} placement="left" describeChild>
                    <WorkflowIcon />
                </Tooltip>
            }
            isLoading={button.isRunning}
            disabled={disabled}
        />
    ));
}
